import { useAuth } from '@forwardflow/context/AuthContext'
import { useGetOrganizationList } from '@forwardflow/hooks/customHooks/organizationList'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { isOperatorEmail, buildOrgSwitchRedirectUrl } from '@forwardflow/utils/app.utils'
import { message, Select } from 'antd'
import { useEffect, useState, useMemo } from 'react'
import { useAppStore } from '@forwardflow/store'
import { SearchField } from '@forwardflow/ui/SearchField'

export const SelectOperatorOrganization = () => {
   const LIMIT = 100
   const { navigateUser } = useUserNavigation()
   const { currentOrganization } = useAppStore()
   const [hasMore, setHasMore] = useState(true)
   const [isLoadingMore, setIsLoadingMore] = useState(false)
   const [searchValue, setSearchValue] = useState('')
   const [organizationParams, setOrganizationParams] = useState({
      page: 1,
      limit: LIMIT,
   })

   const { auth } = useAuth()

   const shouldFetch = isOperatorEmail(auth.user?.email || '')
   const { data: organizationList, isLoading } = useGetOrganizationList({
      ...organizationParams,
      shouldFetch,
   })
   const [allOrganizations, setAllOrganizations] = useState<any[]>([])

   const organizationOptions = allOrganizations?.map((item) => {
      return {
         value: item.id,
         label: item.organizationName + ' - ' + item.id,
      }
   })

   const filteredOptions = useMemo(() => {
      if (!searchValue) return organizationOptions
      return organizationOptions.filter((option) =>
         option.label.toLowerCase().includes(searchValue.toLowerCase())
      )
   }, [organizationOptions, searchValue])

   const onSelectOrganization = async (value: string) => {
      try {
         message.success('Organization changed successfully')
         await navigateUser({
            currentOrgId: value,
            searchParams: location.search,
            redirectTo: buildOrgSwitchRedirectUrl(value),
         })
      } catch (error) {
         console.log('error', error)
      }
   }

   const loadMoreOrganizations = () => {
      if (!isLoadingMore && !isLoading && hasMore) {
         setIsLoadingMore(true)
         setOrganizationParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   useEffect(() => {
      if (organizationList && organizationList.length > 0) {
         setAllOrganizations((prev) => {
            const combined = [...prev, ...organizationList]
            return Array.from(
               new Map(combined.map((item) => [item.id, item])).values()
            )
         })

         if (organizationList.length < LIMIT) {
            setHasMore(false)
         }
         setIsLoadingMore(false)
      }
   }, [organizationList])

   return (
      <Select
         className="ml-[10px] w-[200px] dashboard-select"
         placeholder="Select Organization"
         options={filteredOptions}
         onChange={onSelectOrganization}
         value={currentOrganization || ''}
         loading={isLoading || isLoadingMore}
         notFoundContent={<div className="hidden" />}
         onPopupScroll={(e) => {
            const target = e.target as HTMLDivElement
            const threshold = 50
            const isNearBottom =
               target.scrollTop + target.offsetHeight >=
               target.scrollHeight - threshold

            if (isNearBottom && hasMore && !searchValue) {
               loadMoreOrganizations()
            }
         }}
         popupRender={(menu) => (
            <div>
               <div>
                  <SearchField
                     placeholder="Search organizations"
                     className="!text-[14px] !bg-transparent !text-white gap-1"
                     value={searchValue}
                     onChange={(value) => setSearchValue(value)}
                     iconClassName='!fill-white/70 scale-75 !px-0 !ml-2'
                  />
               </div>
               
               {menu}
               
               {!searchValue && isLoadingMore && (
                  <div className="text-center text-white/70 text-xs py-2">
                     Loading more...
                  </div>
               )}
               {searchValue && filteredOptions.length === 0 && (
                  <div className="text-center py-2 text-white/70 text-xs">
                     No organizations found
                  </div>
               )}
            </div>
         )}
      />
   )
}